<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import CounselorProfileComponent from '@/components/modal/CounselorProfileComponent.vue';
    import CounselingRoomGuidanceComponent from '@/components/modal/CounselingRoomGuidanceComponent.vue';
    import i18n from '@/common/i18n';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Vue
    import { reactive, ref, inject } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';

    // Model
    import utils from '@/commonProject/helpers/utils.js';

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const reserveStore = useReserveStore();
    const commonStore = useCommonStore();
    const from = commonStore.from;
    const counselorProfileModalDispFlg = ref('');
    const counselorProfileComponent = ref();
    const counselingRoomGuidanceModalDispFlg = ref('');
    const counselingRoomGuidanceComponent = ref();
    const languageStore = useLanguageStore();

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);

    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // ----------------------------
    // API関数
    // ----------------------------
    // 予約登録API
    async function getCreate() {
        const response = await reserveStore.getReserveCreateAction(API_URL, removeEmptyKey(requestData));

        if (response.success) {
            // リクエスト情報クリア
            reserveStore.clearRequestData();

            if (response.linkUrl != null && response.linkUrl != '') {
                reserveStore.reservationPk = response.reservationPk;
                window.location.href = response.linkUrl;
                return;
            }
            // 「予約(予約完了)」画面に遷移する
            router.push('/reserve/planning/complete');
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // 予約更新API
    async function getUpdate() {
        const requestBody = {
            ReservationPk: reserveStore.reservationPk,
            CngReserveType: requestData.CngReserveType,
        };
        // 施設PK
        if (!utils.isNullEmpty(requestData.RoomPk)) {
            requestBody['RoomPk'] = requestData.RoomPk;
        }
        // カウンセラーPK
        if (!utils.isNullEmpty(requestData.CounselorPk)) {
            requestBody['CounselorPk'] = requestData.CounselorPk;
        }
        // 予約日時
        if (!utils.isNullEmpty(requestData.ReservationDt)) {
            requestBody['ReservationDt'] = requestData.ReservationDt;
        }
        // 地域PK
        if (!utils.isNullEmpty(requestData.RegionPk)) {
            requestBody['RegionPk'] = requestData.RegionPk;
        }
        // 地区PK
        if (!utils.isNullEmpty(requestData.AreaPk)) {
            requestBody['AreaPk'] = requestData.AreaPk;
        }
        // アレンジ希望日時1
        if (!utils.isNullEmpty(requestData.RequestDt1Start)) {
            requestBody['RequestDt1Start'] = requestData.RequestDt1Start;
        }
        // アレンジ希望日時1
        if (!utils.isNullEmpty(requestData.RequestDt1End)) {
            requestBody['RequestDt1End'] = requestData.RequestDt1End;
        }
        // アレンジ希望日時2
        if (!utils.isNullEmpty(requestData.RequestDt2Start)) {
            requestBody['RequestDt2Start'] = requestData.RequestDt2Start;
        }
        // アレンジ希望日時2
        if (!utils.isNullEmpty(requestData.RequestDt2End)) {
            requestBody['RequestDt2End'] = requestData.RequestDt2End;
        }
        // アレンジ希望日時3
        if (!utils.isNullEmpty(requestData.RequestDt3Start)) {
            requestBody['RequestDt3Start'] = requestData.RequestDt3Start;
        }
        // アレンジ希望日時3
        if (!utils.isNullEmpty(requestData.RequestDt3End)) {
            requestBody['RequestDt3End'] = requestData.RequestDt3End;
        }
        // アレンジ希望日時3
        if (!utils.isNullEmpty(requestData.RequestNote)) {
            requestBody['RequestNote'] = requestData.RequestNote;
        }
        const response = await reserveStore.getReserveUpdateAction(API_URL, requestBody);

        if (response.success) {
            // リクエスト情報クリア
            reserveStore.clearRequestData();

            // 「予約(予約完了)」画面に遷移する
            router.push('/reserve/planning/complete');
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // ----------------------------
    // 関数
    // ----------------------------
    // 戻るボタン押下時
    function onBack() {
        // 前画面からの戻るフラグを有効にする。
        commonStore.reservePageBack = true;

        //router.back();
        // 予約確認画面から予約画面に戻す。
        router.push({ name: 'reserveplanningcounselor' });
    }
    // 確定ボタン押下時
    async function onConfirm() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();
        
        // 予約詳細画面から来た場合
        if (from === 'ReserveDetailView') {
            // 予約更新API
            await getUpdate();
        } else {
            // 予約登録API
            await getCreate();
        }
    }

    // オブジェクトの空の値を削除
    function removeEmptyKey(obj) {
        for (const [key, value] of Object.entries(obj)) {
            if (utils.isNullEmpty(value)) {
                delete obj[key];
            }
        }
        return obj;
    }

    function formatDate(requestDate) {
        if (utils.isNullEmpty(requestDate)) {
            return null;
        }
        const date = new Date(requestDate);

        const daysOfWeekJp = ['日', '月', '火', '水', '木', '金', '土'];
        const monthNamesJp = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
        const daysOfWeekEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        let daysOfWeek = [];
        let monthNames = [];

        // 言語モードに応じて画面に表示する情報を制御する。
        switch (languageStore.getLanguageValue) {
            case '1':
                // 日本語の場合。
                daysOfWeek = daysOfWeekJp;
                monthNames = monthNamesJp;
                break;
            case '2':
                // 英語の場合。
                daysOfWeek = daysOfWeekEn;
                monthNames = monthNamesEn;
                break;
            default:
                // 何もしない。
                break;
        }

        //const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
        //const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
        const dayOfweek = daysOfWeek[date.getDay()];
        const year = date.getFullYear();
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        let format = '';

        // 言語モードに応じて画面に表示する情報を制御する。
        switch (languageStore.getLanguageValue) {
            case '1':
                // 日本語の場合。
                // 「月」は事前に設定されている。
                format = `${year}年${month}${day}日(${dayOfweek}) ${hours}:${minutes}`;
                break;
            case '2':
                // 英語の場合。
                format = `${year}/${month}/${day} (${dayOfweek}) ${hours}:${minutes}`;
                break;
            default:
                // 何もしない。
                break;
        }

        return format;
    }

    /**
     * カウンセラープロフィールモーダルを開くイベント処理
     */
    async function counselorProfileComponentOpen(CounselorPk) {
        counselorProfileComponent.value.openModal(API_URL, CounselorPk);
        counselorProfileModalDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }

    async function counselorProfileComponentClose() {
        counselorProfileModalDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    /**
     * ルーム・施設モーダルを開くイベント処理
     */
    async function counselingRoomGuidanceOpen(RoomPk) {
        if (utils.isNullEmpty(RoomPk)) {
            messageDialog('error', i18n.global.t('reservePlanningCounselor.parts.button1'));
            return;
        }
        counselingRoomGuidanceComponent.value.openModal(API_URL, RoomPk);
        counselingRoomGuidanceModalDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }

    async function counselingRoomGuidanceClose() {
        counselingRoomGuidanceModalDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }
</script>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: counselorProfileModalDispFlg }">
            <CounselorProfileComponent ref="counselorProfileComponent" @counselorProfileModal:close="counselorProfileComponentClose" />
            <!--カウンセリングプロフィールモーダル関連-->
        </section>
        <section id="overlay" :style="{ display: counselingRoomGuidanceModalDispFlg }">
            <CounselingRoomGuidanceComponent ref="counselingRoomGuidanceComponent" @counselingRoomGuidanceModal:close="counselingRoomGuidanceClose" />
            <!--カウンセリングルーム案内モーダル関連-->
        </section>
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningConfirm.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step5.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step5.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step5.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step5_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step5_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step5_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                </h3>
                <section class="whiteBoxContents scroll">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step5.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step5.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step5.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step5_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step5_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step5_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <section class="form">
                        <div class="formBox">
                            <!--予約情報ブロック-->
                            <h4>{{ $t('reservePlanningConfirm.parts.blockTitle1') }}</h4>
                            <dl class="line">
                                <!--ご予約日時-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl1') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ formatDate(reserveStore.rsvdtlRequest.ReservationDt) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご利用方法-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl2') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd v-if="requestData.CngMethodType === '1'">
                                    <ul class="formInput" v-if="!utils.isNullEmpty(reserveStore.rsvdtlRequest.RoomPk)">
                                        <li style="max-width: 350px">
                                            <span class="noTextOverlow">{{ $t('reserveCancelConfirm.message.message1') + reserveStore.getRoomName(reserveStore.rsvdtlRequest.RoomPk) }}</span>
                                        </li>
                                        <li>
                                            <!--ルーム・施設ボタン-->
                                            <button type="button" class="innerBtn" @click="counselingRoomGuidanceOpen(reserveStore.rsvdtlRequest.RoomPk)">{{ $t('reserveCancelConfirm.parts.button1') }}</button>
                                        </li>
                                    </ul>
                                </dd>
                                <dd v-else-if="requestData.CngMethodType === '2'">
                                    <ul class="formInput">
                                        <li v-if="reserveStore.serviceDetail.ServiceTelMet === '1'">
                                            <span class="noTextOverlow">{{ $t('reserveCancelConfirm.message.message2') }}</span>
                                        </li>
                                        <li v-else>
                                            <span class="noTextOverlow">{{ $t('reserveCancelConfirm.message.message3') }}</span>
                                        </li>
                                    </ul>
                                </dd>
                                <dd v-else-if="requestData.CngMethodType === '3'">
                                    <ul class="formInput">
                                        <li>
                                            <span class="noTextOverlow">{{ $t('reserveCancelConfirm.message.message4') }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line" v-if="reserveStore.serviceDetail.PaymentFlag && reserveStore.rsvdtlRequest.CngReserveType === '1'">
                                <!--決済期限-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl14') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <div style="word-break: break-all">
                                                <span>{{ $t('reservePlanningConfirm.message.message1') }}</span>
                                            </div>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--カウンセラー-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl3') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd v-if="requestData.CngReserveType === '1'">
                                    <ul class="formInput">
                                        <li style="max-width: 350px">
                                            <span>{{ reserveStore.getCounselorName(reserveStore.rsvdtlRequest.CounselorPk) }}</span>
                                        </li>
                                        <li>
                                            <!--プロフィールボタン-->
                                            <button type="button" class="innerBtn" @click="counselorProfileComponentOpen(reserveStore.rsvdtlRequest.CounselorPk)">{{ $t('reserveCancelConfirm.parts.button2') }}</button>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </div>
                        <div class="formBox">
                            <!--予約詳細ブロック-->
                            <h4>{{ $t('reservePlanningConfirm.parts.blockTitle2') }}</h4>
                            <dl class="line">
                                <!--サービス-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl4') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li v-if="utils.isNullEmpty(reserveStore.reserveDetail.ServiceNm)">
                                            <span>{{ reserveStore.getServiceName(reserveStore.rsvdtlRequest.ServicePk) }}</span>
                                        </li>
                                        <li v-else>
                                            <span>{{ reserveStore.reserveDetail.ServiceNm }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご相談内容1-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl5') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li v-if="utils.isNullEmpty(reserveStore.reserveDetail.IssueNm1)">
                                            <span>{{ reserveStore.getIssueName(reserveStore.rsvdtlRequest.IssuePk1) }}</span>
                                        </li>
                                        <li v-else>
                                            <span>{{ reserveStore.reserveDetail.IssueNm1 }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご相談内容2-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl6') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li v-if="utils.isNullEmpty(reserveStore.reserveDetail.IssueNm2)">
                                            <span>{{ reserveStore.getIssueName(reserveStore.rsvdtlRequest.IssuePk2) }}</span>
                                        </li>
                                        <li v-else>
                                            <span>{{ reserveStore.reserveDetail.IssueNm2 }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご利用言語-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl7') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li v-if="utils.isNullEmpty(reserveStore.reserveDetail.CngLanguageNm)">
                                            <span>{{ reserveStore.getLanguageName(reserveStore.rsvdtlRequest.CngLanguagePk) }}</span>
                                        </li>
                                        <li v-else>
                                            <span>{{ reserveStore.reserveDetail.CngLanguageNm }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </div>
                        <div class="formBox" v-if="reserveStore.rsvdtlRequest.CngReserveType === '2'">
                            <!--ご希望内容ブロック（「ご希望の場所・時間帯をご連絡」タブからの登録した場合）-->
                            <h4>{{ $t('reservePlanningConfirm.parts.blockTitle3') }}</h4>
                            <dl class="line">
                                <!--ご希望地域-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl8') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ reserveStore.getRegionName(reserveStore.rsvdtlRequest.RegionPk) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご希望地区-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl9') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ reserveStore.getAreaName(reserveStore.rsvdtlRequest.AreaPk) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご希望日時1-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl10') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ reserveStore.formattedTime(reserveStore.rsvdtlRequest.RequestDt1Start, reserveStore.rsvdtlRequest.RequestDt1End) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご希望日時2-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl11') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ reserveStore.formattedTime(reserveStore.rsvdtlRequest.RequestDt2Start, reserveStore.rsvdtlRequest.RequestDt2End) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご希望日時3-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl12') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ reserveStore.formattedTime(reserveStore.rsvdtlRequest.RequestDt3Start, reserveStore.rsvdtlRequest.RequestDt3End) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line wrap">
                                <!--ご連絡事項-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningConfirm.parts.dl13') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <p>{{ reserveStore.rsvdtlRequest.RequestNote }}</p>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </div>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <button type="button" class="btnPrimary" @click="onConfirm" :disabled="disabledStore.isDisabled">{{ $t('reservePlanningConfirm.parts.button1') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
